import { useState, useEffect, type ReactNode } from "react";
import { toast } from "react-hot-toast";
import { campaignService } from "../../services/campaign.service";
import { productService } from "../../services/product.service";
import type { CampaignFormData, Product } from "../../types/campaign";
import ProductMultiSelect from "./ProductMultiSelect";
import { type RadioOption } from "../ui/custom-radio";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Spinner } from "../ui/spinner";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { cn } from "@/lib/utils";

interface CreateCampaignFormProps {
  onSave: () => void;
  /** Leaves the form without saving. */
  onCancel?: () => void;
  campaignId?: string;
  initialData?: CampaignFormData;
}

const appliesToOptions: RadioOption[] = [
  { value: "all", label: "All products" },
  { value: "specific", label: "Specific products" },
];

const minimumRequirementsOptions: RadioOption[] = [
  { value: "none", label: "No minimum" },
  { value: "amount", label: "Minimum order amount" },
  { value: "quantity", label: "Minimum number of items" },
];

const maxDiscountUsesOptions: RadioOption[] = [
  { value: "unlimited", label: "Unlimited" },
  { value: "limited", label: "Limit total uses" },
  { value: "onePerCustomer", label: "One use per customer" },
];

/** One labelled field: label above, control, one optional line of help. */
function Field({
  id,
  label,
  help,
  children,
  className,
}: {
  id?: string;
  label: string;
  help?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div id={id} className={cn("space-y-1.5", className)}>
      <Label className="text-foreground">{label}</Label>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

function RadioRow({
  id,
  value,
  label,
  disabled,
}: {
  id: string;
  value: string;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <RadioGroupItem value={value} id={id} disabled={disabled} />
      <Label htmlFor={id} className="font-normal text-foreground">
        {label}
      </Label>
    </div>
  );
}

export default function CreateCampaignForm({
  onSave,
  onCancel,
  campaignId,
  initialData,
}: CreateCampaignFormProps) {
  const isEditMode = !!campaignId;
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const emptyFormData = (): CampaignFormData => ({
    campaignName: "",
    discountCode: "",
    discountType: "percentage",
    discountValue: "",
    currency: "USD",
    minimumRequirements: "none",
    minimumAmount: "",
    minimumQuantity: "",
    maxDiscountUses: "unlimited",
    maxUsesLimit: "",
    appliesTo: "all",
    selectedProductIds: [],
    combinations: {
      productDiscounts: false,
      orderDiscounts: false,
      shippingDiscounts: false,
    },
  });

  const getInitialFormData = (): CampaignFormData => {
    if (initialData) {
      return initialData;
    }
    return emptyFormData();
  };

  const [formData, setFormData] = useState<CampaignFormData>(
    getInitialFormData()
  );

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await productService.findAll();

      // Handle the correct response structure based on Products page
      let productData = [];
      if (response.products) {
        productData = response.products;
      } else if (response.data) {
        productData = response.data;
      } else if (Array.isArray(response)) {
        productData = response;
      } else {
        console.warn("Unexpected product response structure:", response);
      }
      setProducts(productData);
    } catch (error) {
      console.error("Failed to load products:", error);
      toast.error("Could not load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleInputChange = (
    field: keyof CampaignFormData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCombinationChange = (
    field: keyof CampaignFormData["combinations"],
    value: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      combinations: {
        ...prev.combinations,
        [field]: value,
      },
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.campaignName.trim()) {
      toast.error("Enter a campaign name");
      return false;
    }

    if (!formData.discountCode.trim()) {
      toast.error("Enter a discount code");
      return false;
    }

    const discountValue = parseFloat(formData.discountValue.toString());
    if (isNaN(discountValue) || discountValue <= 0) {
      toast.error("Discount value must be greater than 0");
      return false;
    }

    if (formData.discountType === "percentage" && discountValue > 100) {
      toast.error("Percentage discount cannot exceed 100%");
      return false;
    }

    if (formData.minimumRequirements === "amount") {
      const minimumAmount =
        formData.minimumAmount === ""
          ? NaN
          : Number(formData.minimumAmount);
      if (isNaN(minimumAmount) || minimumAmount <= 0) {
        toast.error("Minimum amount must be greater than 0");
        return false;
      }
    }

    if (formData.minimumRequirements === "quantity") {
      const minimumQuantity =
        formData.minimumQuantity === ""
          ? NaN
          : Number(formData.minimumQuantity);
      if (isNaN(minimumQuantity) || minimumQuantity <= 0) {
        toast.error("Minimum quantity must be greater than 0");
        return false;
      }
    }

    if (formData.maxDiscountUses === "limited") {
      const maxUsesLimit =
        formData.maxUsesLimit === "" ? NaN : Number(formData.maxUsesLimit);
      if (isNaN(maxUsesLimit) || maxUsesLimit <= 0) {
        toast.error("Usage limit must be greater than 0");
        return false;
      }
    }

    if (
      formData.appliesTo === "specific" &&
      formData.selectedProductIds.length === 0
    ) {
      toast.error("Select at least one product");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);

      const buildMinimumRequirement = () => {
        if (formData.minimumRequirements === "amount") {
          return {
            subtotal: {
              greaterThanOrEqualToSubtotal: Number(
                formData.minimumAmount
              ).toString(),
            },
            quantity: { greaterThanOrEqualToQuantity: null },
          };
        }
        if (formData.minimumRequirements === "quantity") {
          return {
            quantity: {
              // Shopify UnsignedInt64 requires a string; backend also stringifies
              greaterThanOrEqualToQuantity: Number(
                formData.minimumQuantity
              ).toString(),
            },
            subtotal: { greaterThanOrEqualToSubtotal: null },
          };
        }
        // Clear both so Shopify/DB do not keep a previous requirement
        return {
          quantity: { greaterThanOrEqualToQuantity: null },
          subtotal: { greaterThanOrEqualToSubtotal: null },
        };
      };

      // Transform form data to match API expectations
      const campaignData = {
        title: formData.campaignName,
        code: formData.discountCode,
        startsAt: new Date().toISOString(),
        usageLimit:
          formData.maxDiscountUses === "limited"
            ? Number(formData.maxUsesLimit)
            : null,
        appliesOncePerCustomer: formData.maxDiscountUses === "onePerCustomer",
        minimumRequirement: buildMinimumRequirement(),
        customerGets: {
          value:
            formData.discountType === "percentage"
              ? {
                  percentage:
                    parseFloat(formData.discountValue.toString()) / 100,
                } // Convert percentage to decimal
              : {
                  discountAmount: {
                    amount: formData.discountValue.toString(),
                  },
                },
          // Always include items to update product selection
          // This ensures deleted products are removed and selection is always current
          items: (() => {
            if (formData.appliesTo === "specific") {
              const transformedIds = formData.selectedProductIds.map((id) => {
                // Extract just the numeric ID from the Shopify Global ID
                // gid://shopify/Product/7742264279092 -> 7742264279092
                if (id.startsWith("gid://shopify/Product/")) {
                  return id.split("/").pop() || id;
                }
                return id;
              });
              return { productIds: transformedIds };
            }
            // For "all products", send empty productIds to clear specific selections
            return { all: true };
          })(),
        },
        combinesWith: {
          orderDiscounts: formData.combinations.orderDiscounts,
          productDiscounts: formData.combinations.productDiscounts,
          shippingDiscounts: formData.combinations.shippingDiscounts,
        },
      };

      if (isEditMode && campaignId) {
        await campaignService.updateCampaign(campaignId, campaignData);
        toast.success("Campaign saved");
      } else {
        await campaignService.createCampaign(campaignData);
        toast.success("Campaign created");
      }

      // Reset form
      setFormData(emptyFormData());

      // Call onSave to refresh campaigns list
      onSave();
    } catch (error: unknown) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} campaign:`,
        error
      );
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        `Could not ${isEditMode ? "save" : "create"} the campaign`;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isLoading) return; // Prevent cancel during submission
    if (onCancel) {
      onCancel();
      return;
    }
    setFormData(emptyFormData());
  };

  const handleNumberInputChange = (
    field: "minimumAmount" | "minimumQuantity" | "maxUsesLimit",
    rawValue: string,
    parse: (value: string) => number
  ) => {
    if (rawValue === "") {
      handleInputChange(field, "");
      return;
    }
    const parsed = parse(rawValue);
    if (!isNaN(parsed)) {
      handleInputChange(field, parsed);
    }
  };

  const inputsDisabled = isLoading;

  return (
    <div className="max-w-3xl space-y-4">
      <DashboardCard title="Details" bodyClassName="space-y-5 px-5 pb-5">
        <Field
          id="CCF-campaign-name"
          label="Campaign name"
          help="Only you see this name"
        >
          <Input
            value={formData.campaignName}
            onChange={(e) => handleInputChange("campaignName", e.target.value)}
            disabled={inputsDisabled}
            placeholder="Summer launch"
            autoComplete="off"
          />
        </Field>
        <Field
          id="CCF-discount-code"
          label="Discount code"
          help="Affiliates share this code and customers enter it at checkout"
        >
          <Input
            value={formData.discountCode}
            onChange={(e) =>
              handleInputChange("discountCode", e.target.value.toUpperCase())
            }
            disabled={inputsDisabled}
            placeholder="SUMMER25"
            autoComplete="off"
          />
        </Field>
      </DashboardCard>

      <DashboardCard title="Discount" bodyClassName="space-y-5 px-5 pb-5">
        <Field
          id="CCF-discount-value"
          label="Discount value"
          help="What the customer gets off their order"
        >
          <div className="flex max-w-sm gap-2">
            <Select
              value={formData.discountType}
              onValueChange={(value) => handleInputChange("discountType", value)}
              disabled={inputsDisabled}
            >
              <SelectTrigger className="w-40 shrink-0" aria-label="Discount type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage</SelectItem>
                <SelectItem value="fixed">Fixed amount</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                {formData.discountType === "fixed" ? "$" : "%"}
              </span>
              <Input
                type="number"
                inputMode="decimal"
                value={formData.discountValue}
                onChange={(e) =>
                  handleInputChange(
                    "discountValue",
                    e.target.value === "" ? "" : parseFloat(e.target.value)
                  )
                }
                disabled={inputsDisabled}
                placeholder="0"
                min="0"
                max={formData.discountType === "percentage" ? "100" : undefined}
                className="pl-7 tabular-nums"
              />
            </div>
          </div>
        </Field>

        <Field id="CCF-applies-to" label="Applies to">
          <RadioGroup
            value={formData.appliesTo}
            onValueChange={(value) => handleInputChange("appliesTo", value)}
            name="appliesTo"
            disabled={inputsDisabled}
            className="gap-2"
          >
            {appliesToOptions.map((option) => (
              <RadioRow
                key={option.value}
                id={`appliesTo-${option.value}`}
                value={option.value}
                label={option.label}
                disabled={inputsDisabled}
              />
            ))}
          </RadioGroup>
          {formData.appliesTo === "specific" && (
            <div className="max-w-sm pt-1">
              <ProductMultiSelect
                products={products}
                selectedIds={formData.selectedProductIds}
                onChange={(ids) =>
                  setFormData((prev) => ({ ...prev, selectedProductIds: ids }))
                }
                loading={loadingProducts}
                disabled={inputsDisabled}
                required
              />
            </div>
          )}
        </Field>
      </DashboardCard>

      <DashboardCard title="Limits" bodyClassName="space-y-5 px-5 pb-5">
        <Field id="CCF-minimum-requirements" label="Minimum purchase">
          <RadioGroup
            value={formData.minimumRequirements}
            onValueChange={(value) =>
              handleInputChange("minimumRequirements", value)
            }
            name="minimumRequirements"
            disabled={inputsDisabled}
            className="gap-2"
          >
            {minimumRequirementsOptions.map((option) => (
              <RadioRow
                key={option.value}
                id={`minimumRequirements-${option.value}`}
                value={option.value}
                label={option.label}
                disabled={inputsDisabled}
              />
            ))}
          </RadioGroup>
          {formData.minimumRequirements === "amount" && (
            <div className="relative max-w-[12rem] pt-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center pt-1 text-sm text-muted-foreground">
                $
              </span>
              <Input
                type="number"
                inputMode="decimal"
                value={formData.minimumAmount}
                onChange={(e) =>
                  handleNumberInputChange("minimumAmount", e.target.value, parseFloat)
                }
                disabled={inputsDisabled}
                placeholder="0.00"
                min="0"
                className="pl-7 tabular-nums"
                aria-label="Minimum order amount"
              />
            </div>
          )}
          {formData.minimumRequirements === "quantity" && (
            <div className="max-w-[12rem] pt-1">
              <Input
                type="number"
                inputMode="numeric"
                value={formData.minimumQuantity}
                onChange={(e) =>
                  handleNumberInputChange("minimumQuantity", e.target.value, parseInt)
                }
                disabled={inputsDisabled}
                placeholder="0"
                min="0"
                className="tabular-nums"
                aria-label="Minimum number of items"
              />
            </div>
          )}
        </Field>

        <Field id="CCF-maximum-discount-uses" label="Usage limit">
          <RadioGroup
            value={formData.maxDiscountUses}
            onValueChange={(value) => handleInputChange("maxDiscountUses", value)}
            name="maxDiscountUses"
            disabled={inputsDisabled}
            className="gap-2"
          >
            {maxDiscountUsesOptions.map((option) => (
              <RadioRow
                key={option.value}
                id={`maxDiscountUses-${option.value}`}
                value={option.value}
                label={option.label}
                disabled={inputsDisabled}
              />
            ))}
          </RadioGroup>
          {formData.maxDiscountUses === "limited" && (
            <div className="max-w-[12rem] pt-1">
              <Input
                type="number"
                inputMode="numeric"
                value={formData.maxUsesLimit}
                onChange={(e) =>
                  handleNumberInputChange("maxUsesLimit", e.target.value, (value) =>
                    parseInt(value, 10)
                  )
                }
                disabled={inputsDisabled}
                placeholder="0"
                min="0"
                className="tabular-nums"
                aria-label="Maximum number of uses"
              />
            </div>
          )}
        </Field>
      </DashboardCard>

      <DashboardCard title="Combinations" bodyClassName="px-5 pb-5">
        <Field id="CCF-combinations" label="This code can be combined with">
          <div className="space-y-2 pt-1">
            {(
              [
                ["productDiscounts", "Product discounts"],
                ["orderDiscounts", "Order discounts"],
                ["shippingDiscounts", "Shipping discounts"],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox
                  id={key}
                  checked={formData.combinations[key]}
                  onCheckedChange={(checked) =>
                    handleCombinationChange(key, checked as boolean)
                  }
                  disabled={inputsDisabled}
                />
                <Label htmlFor={key} className="font-normal text-foreground">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </Field>
      </DashboardCard>

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          id="CCF-save-button"
          type="button"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading && (
            <Spinner size="sm" className="border-primary-foreground border-t-transparent" />
          )}
          {isEditMode ? "Save campaign" : "Create campaign"}
        </Button>
      </div>
    </div>
  );
}
